/**
 * TwoCellTransformationsPanel — before/after view of 2-categorical
 * morphism transformations.
 *
 * Each scenario shows the ungoverned 2-cell that admits the attack and
 * the SDP-typed 2-cell that re-types the same composition so the attack
 * becomes structurally inexpressible. DiffViewer surfaces the rewrite
 * inline; a Drawer hosts the long-form detail context (adjunction
 * lineage, ICG envelope, concrete-attack annotation) for the focused
 * 2-cell so the reader can drill down without losing scroll position.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo, useState } from "react";
import { DiffViewer, Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerTitle, DrawerTrigger } from "@csk/demo-ui";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import transformationsData from "../data/twoCellTransformations.json" with { type: "json" };
import sdpsData from "../data/sdps.json" with { type: "json" };

interface Transformation {
  readonly id: string;
  readonly sdp: string;
  readonly title: string;
  readonly before: string;
  readonly after: string;
  readonly synopsis: string;
}

interface SdpEntry {
  readonly code: string;
  readonly name: string;
  readonly adjunction: string;
  readonly neutralizationTheorems: readonly string[];
  readonly dualPairGuarantee: string | null;
  readonly icg: string;
  readonly color: string;
  readonly aiScenario: string;
  readonly aiSummary: string;
  readonly unitDescription: string;
  readonly counitDescription: string;
  readonly concreteAttack: string;
}

const TRANSFORMATIONS: readonly Transformation[] =
  transformationsData.transformations;
const SDPS: readonly SdpEntry[] = sdpsData.sdps;

function sdpFor(code: string): SdpEntry | undefined {
  return SDPS.find((entry) => entry.code === code);
}

export function TwoCellTransformationsPanel(): React.JSX.Element {
  const [activeId, setActiveId] = useState<string>(
    TRANSFORMATIONS[0]?.id ?? "",
  );
  const transformation = useMemo(
    () =>
      TRANSFORMATIONS.find((entry) => entry.id === activeId) ??
      TRANSFORMATIONS[0],
    [activeId],
  );
  const sdp = sdpFor(transformation.sdp);
  const accent = sdp?.color ?? panelTokens.accent;

  return (
    <PanelSurface.PanelShell
      title="2-Cell Transformations"
      subtitle="Re-type the cell; the attack becomes structurally inexpressible"
      accentColor={accent}
    >
      <PanelSurface.Card glowColor={`${accent}22`}>
        <p className="m-0 text-[13.5px] leading-[1.75] text-[var(--tcc-text)]">
          Pick a scenario; the left/right diff shows the ungoverned 2-cell
          (admits the attack) against the SDP-typed 2-cell (the same
          composition, retyped so the attack term is no longer constructible).
          The detail drawer carries the adjunction lineage, the ICG envelope,
          and the concrete attack the typing neutralizes by construction.
        </p>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Pick a transformation</PanelSurface.SectionHeading>
      <PanelSurface.ChipRow
        ariaLabel="2-cell transformation selection"
        activeId={activeId}
        onChange={setActiveId}
        items={TRANSFORMATIONS.map((entry) => {
          const entrySdp = sdpFor(entry.sdp);
          return {
            id: entry.id,
            label: `${entry.sdp} · ${entry.title}`,
            color: entrySdp?.color ?? panelTokens.accent,
          };
        })}
      />

      <PanelSurface.SectionHeading>The re-typing</PanelSurface.SectionHeading>
      <PanelSurface.Card style={{ borderLeft: `3px solid ${accent}` }}>
        <div className="mb-2.5 flex flex-wrap gap-2">
          {PanelSurface.badge(transformation.sdp, accent)}
          {sdp !== undefined && PanelSurface.badge(sdp.name, accent)}
          {sdp?.neutralizationTheorems.map((thm) => (
            <React.Fragment key={thm}>
              {PanelSurface.badge(thm, panelTokens.accentPurple)}
            </React.Fragment>
          ))}
          <Drawer>
            <DrawerTrigger asChild>
              <button
                type="button"
                data-testid={`open-drawer-${transformation.id}`}
                className="cursor-pointer rounded-full border border-solid px-3 py-1 font-mono text-[11px] font-bold tracking-[0.05em]"
                style={{
                  background: `${accent}22`,
                  borderColor: `${accent}55`,
                  color: accent,
                }}
              >
                Open 2-cell detail ▸
              </button>
            </DrawerTrigger>
            <DrawerContent side="right" size="min(28rem, calc(100vw - 2rem))">
              <DrawerTitle>
                {transformation.sdp} · {transformation.title}
              </DrawerTitle>
              <DrawerDescription>{transformation.synopsis}</DrawerDescription>
              {sdp !== undefined && (
                <div className="flex flex-col gap-2.5 text-[12.5px] text-[#1f2937]">
                  <div>
                    <span className="font-bold">Adjunction:</span>{" "}
                    <code>{sdp.adjunction}</code>
                  </div>
                  <div>
                    <span className="font-bold">ICG envelope:</span>{" "}
                    <code>{sdp.icg}</code>
                    {sdp.dualPairGuarantee !== null
                      ? ` (dual-pair → ${sdp.dualPairGuarantee})`
                      : ""}
                  </div>
                  <div>
                    <span className="font-bold">Unit η:</span>{" "}
                    {sdp.unitDescription}
                  </div>
                  <div>
                    <span className="font-bold">Counit ε:</span>{" "}
                    {sdp.counitDescription}
                  </div>
                  <div>
                    <span className="font-bold">AI scenario:</span>{" "}
                    {sdp.aiScenario}
                  </div>
                  <div>
                    <span className="font-bold">
                      Concrete attack neutralized:
                    </span>{" "}
                    {sdp.concreteAttack}
                  </div>
                </div>
              )}
              <DrawerClose asChild>
                <button
                  type="button"
                  className="mt-3 cursor-pointer self-end rounded-md border border-[#cbd5e1] bg-[#f8fafc] px-3.5 py-1.5 text-[12px] text-[#0f172a]"
                >
                  Close
                </button>
              </DrawerClose>
            </DrawerContent>
          </Drawer>
        </div>
        <DiffViewer
          mode="split"
          beforeLabel="ungoverned 2-cell"
          afterLabel={`${transformation.sdp}-typed 2-cell`}
          before={transformation.before}
          after={transformation.after}
        />
        <p className="mt-3 text-[12.5px] italic leading-[1.6] text-[var(--tcc-muted)]">
          {transformation.synopsis}
        </p>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
